import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import SocialLoginButtons from '../components/auth/SocialLoginButtons';
import { parseAuthError, authLog, isProfileComplete } from '../utils/authUtils';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [siteName, setSiteName] = useState('SlabReality');
  const { login, logout, user, profile, refreshProfile, isProfileComplete: profileComplete } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Reset scroll on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const from = location.state?.from || '/dashboard';
  const pendingApproval = location.state?.pendingApproval;
  const pendingMessage = location.state?.message;

  // If already logged in, redirect appropriately
  useEffect(() => {
    if (user && !loading) {
      authLog('Login: User already logged in', { email: user.email, profileComplete });
      if (profileComplete) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    }
  }, [user, profileComplete, loading, navigate]);

  // If already logged in, show option to go to dashboard or logout
  if (user) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="w-full max-w-md p-8 text-center">
          <h1 className="text-2xl font-semibold text-white mb-2">Sei già loggato</h1>
          <p className="text-white/60 mb-6">
            Connesso come: <strong className="text-white">{user.email}</strong>
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate(profileComplete ? '/dashboard' : '/onboarding')}
              className="w-full bg-sr-gold hover:bg-sr-gold-hover text-black"
            >
              {profileComplete ? 'Vai alla Dashboard' : 'Completa il Profilo'}
            </Button>
            <Button 
              onClick={async () => {
                await logout();
                window.location.reload();
              }}
              variant="ghost"
              className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    authLog('Login: Attempting login', { email });
    
    try {
      await login(email, password);
      
      authLog('Login: Success, refreshing profile...');
      
      // Refresh profile to check completeness
      const profileData = await refreshProfile();
      
      // Check if profile is complete
      const complete = isProfileComplete(profileData);
      
      authLog('Login: Profile check', { complete, profileData });
      
      toast.success(t('auth.welcomeBack'));
      
      if (complete) {
        authLog('Login: Navigating to dashboard');
        navigate(from, { replace: true });
      } else {
        authLog('Login: Navigating to onboarding');
        navigate('/onboarding', { replace: true });
      }
    } catch (error) {
      authLog('Login: Error', { error: error.message });
      toast.error(parseAuthError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex">
      {/* Left side - Quarry Image with gradient */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1714659269550-20a99b2f5faa?w=1200&h=1600&fit=crop)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#0a0a0f] via-[#0a0a0f]/50 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <h2 className="text-3xl font-bold text-white mb-4">
            Il cuore della pietra naturale
          </h2>
          <p className="text-white/70">
            Direttamente dalle cave più pregiate d'Italia, materiali unici per i tuoi progetti.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {t('common.backToHome')}
          </button>
          
          <Link to="/" className="text-2xl font-bold text-white mb-8 block" data-testid="login-logo">
            {siteName}
          </Link>
          
          {/* Pending Approval Banner */}
          {pendingApproval && (
            <div className="mb-6 p-4 bg-sr-gold/10 border border-sr-gold/30 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-sr-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sr-gold-light text-lg">⏳</span>
                </div>
                <div>
                  <h3 className="font-medium text-sr-gold-light mb-1">{t('auth.pendingApprovalTitle')}</h3>
                  <p className="text-sm text-sr-gold-light/70">
                    {pendingMessage || t('auth.pendingApprovalMessage')}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <h1 className="text-3xl md:text-4xl font-medium text-white mb-2" data-testid="login-title">
            {t('auth.welcomeBack')}
          </h1>
          <p className="text-white/60 mb-8">
            {t('auth.loginSubtitle')}
          </p>

          {/* Social Login Buttons */}
          <div className="mb-6">
            <SocialLoginButtons mode="login" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-white/60 text-xs uppercase tracking-wider mb-2 block">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-sr-gold"
                placeholder={t('auth.emailPlaceholder')}
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white/60 text-xs uppercase tracking-wider mb-2 block">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-sr-gold pr-10"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link to="/forgot-password" className="text-sm text-sr-gold-light hover:underline">
                {t('auth.forgotPassword')}
              </Link>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-sr-gold hover:bg-sr-gold-hover text-black font-medium" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-white/60">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-sr-gold-light hover:underline" data-testid="login-register-link">
              {t('auth.createAccount')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
